import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LoadingSpinner, PageContainer, QueryErrorAlert, TabList, TabPanel, TabsRoot, type TabDefinition } from '@shared/components';
import { usePermissions } from '@/features/auth';
import { ThirdPartyAgreementHeader } from '../components/thirdParty/ThirdPartyAgreementHeader';
import { ThirdPartyInvoicingTab } from '../components/thirdParty/ThirdPartyInvoicingTab';
import { ThirdPartyVehiclePanel } from '../components/thirdParty/ThirdPartyVehiclePanel';
import { useThirdPartyAgreementQuery } from '../hooks/useThirdPartyAgreementQuery';
import { useThirdPartyInvoiceVisit } from '../hooks/useThirdPartyInvoiceVisit';
import { useThirdPartyInvoicingQuery } from '../hooks/useThirdPartyInvoicingQuery';
import { thirdPartyBalanceText } from '../utils/thirdPartyAgreementView';
import { isThirdPartyInvoicingPanelFocusable } from '../utils/thirdPartyInvoicingPanelFocus';

type ThirdPartyTab = 'overview' | 'invoicing';

const TABS: TabDefinition<ThirdPartyTab>[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'invoicing', label: 'Invoicing' },
];

const TABS_WITHOUT_INVOICING = TABS.filter((tab) => tab.id !== 'invoicing');

export function ThirdPartyAgreementDetail() {
    const [selectedTab, setActiveTab] = useState<ThirdPartyTab>('overview');
    const canAddInvoice = usePermissions().hasPermission({ resource: 'invoice', action: 'add' });
    const activeTab: ThirdPartyTab = canAddInvoice ? selectedTab : 'overview';
    const [searchParams] = useSearchParams();
    const vrm = searchParams.get('vrm')?.trim() ?? '';
    const rawReference = searchParams.get('reference') ?? '';
    const reference = rawReference === rawReference.trim() ? rawReference : '';
    const query = useThirdPartyAgreementQuery(vrm, reference);
    const invoicingActive = activeTab === 'invoicing';
    const invoicingQuery = useThirdPartyInvoicingQuery(vrm, reference, invoicingActive);
    const invoiceVisit = useThirdPartyInvoiceVisit(vrm, reference);

    if (!vrm || !reference) {
        return (
            <PageContainer>
                <QueryErrorAlert message="Invalid agreement reference." />
            </PageContainer>
        );
    }

    if (query.isLoading) {
        return (
            <PageContainer>
                <LoadingSpinner message="Loading agreement" className="p-4" />
            </PageContainer>
        );
    }

    if (query.isError || !query.data) {
        return (
            <PageContainer>
                <QueryErrorAlert
                    message="Agreement not found or failed to load."
                    onRetry={() => query.refetch()}
                    isRetrying={query.isFetching}
                />
            </PageContainer>
        );
    }

    return (
        <TabsRoot
            tabs={canAddInvoice ? TABS : TABS_WITHOUT_INVOICING}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            idPrefix="third-party-agreement"
            ariaLabel="Agreement sections"
            variant="header"
            focusablePanel={!invoicingActive || isThirdPartyInvoicingPanelFocusable(invoicingQuery)}
        >
            <ThirdPartyAgreementHeader agreement={query.data} tabs={<TabList />} />

            <PageContainer>
                <TabPanel>
                    {activeTab === 'overview' ? (
                        <div className="grid grid-cols-1 gap-6">
                            <ThirdPartyVehiclePanel vehicle={query.data.vehicle} />
                        </div>
                    ) : (
                        <ThirdPartyInvoicingTab
                            vrm={vrm}
                            reference={reference}
                            visit={invoiceVisit}
                            availableBalance={thirdPartyBalanceText(query.data.balanceGrossMinor)}
                        />
                    )}
                </TabPanel>
            </PageContainer>
        </TabsRoot>
    );
}
